import { Injectable } from '@angular/core';
import { Observable, combineLatest, Subject } from 'rxjs';
import { map, takeUntil, switchMap, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { DivineServiceApiService } from './api/divine-service-api.service';
import { DivineServiceStateService, DivineServiceValidationService } from './index';
import { DivineServiceFormBuilderService } from './divine-service-form-builder.service';

import {
  DivineServiceDetailDto,
  CreateDivineServiceDto,
  UpdateDivineServiceDto,
  DivineServiceTypeDto
} from '../models/divine-service.model';
import { DivineServiceState, OptionalFieldsVisibility, INITIAL_DIVINE_SERVICE_STATE } from '../models/divine-service-state.model';

/**
 * Facade Service que orquesta toda la lógica de negocio para Servicios Divinos.
 * 
 * Responsabilidades:
 * - Coordinar entre API, State y Validación
 * - Proporcionar métodos de alto nivel para el componente stepper
 * - Manejar efectos secundarios (carga de datos, guardado, etc)
 * - Simplificar la interfaz para los componentes
 * 
 * Ventajas del patrón Facade:
 * - El stepper no necesita conocer detalles de implementación
 * - Cambios internos no afectan el componente
 * - Fácil de testear en aislamiento
 */
@Injectable({
  providedIn: 'root'
})
export class DivineServiceFacadeService {
  private destroy$ = new Subject<void>();

  // Observables públicos para el stepper
  state$: Observable<DivineServiceState>;
  visibility$: Observable<any>;
  serviceTypes$: Observable<DivineServiceTypeDto[]>;
  isLoading$: Observable<boolean>;
  isSaving$: Observable<boolean>;

  constructor(
    private apiService: DivineServiceApiService,
    private stateService: DivineServiceStateService,
    private validationService: DivineServiceValidationService,
    private formBuilderService: DivineServiceFormBuilderService
  ) {
    // Exponer observables del estado
    this.state$ = this.stateService.getState$();
    this.visibility$ = this.stateService.getOptionalFieldsVisibility$();
    this.isLoading$ = this.state$.pipe(map(s => s.isLoading));
    this.isSaving$ = this.state$.pipe(map(s => s.isSaving));

    // Cargar tipos de servicio al inicializar
    this.serviceTypes$ = this.loadServiceTypes();
  }

  /**
   * Carga los tipos de servicios divinos disponibles
   */
  private loadServiceTypes(): Observable<DivineServiceTypeDto[]> {
    return this.apiService.getDivineServiceTypes().pipe(
      tap(types => console.log('Service types loaded:', types)),
      catchError(error => {
        console.error('Error loading service types:', error);
        return of([]);
      })
    );
  }

  /**
   * Inicializa un nuevo servicio divino vacío
   * Resetea el estado completo para asegurar que no hay valores persistentes del servicio anterior
   */
  initializeNewService(organizationUnitId?: string): void {
    const today = new Date();
    
    // Resetear el estado completo al estado inicial, luego actualizar con nuevos valores
    this.stateService.updateState({
      ...INITIAL_DIVINE_SERVICE_STATE,
      date: today,
      organizationUnitId: organizationUnitId || '',
      isEditMode: true
    });
  }

  /**
   * Carga un servicio divino existente desde el API
   */
  loadDivineService(serviceId: string): Observable<DivineServiceDetailDto> {
    this.stateService.updateState({ isLoading: true });

    return this.apiService.getDivineService(serviceId).pipe(
      tap(service => {
        this.stateService.updateState({
          id: service.id,
          isNewService: false,
          isLoading: false,
          date: new Date(service.startAt),
          type: service.divineServiceTypeId,
          memberInCharge: service.memberInCharge,
          location: service.location,
          organizationUnitId: service.organizationUnitId,
          totalMinors: service.totalMinors,
          totalAdults: service.totalAdults,
          notes: service.notes,
          offeringsARS: service.offeringsARS,
          offeringsUSD: service.offeringsUSD,
          visitsAdults: (service as any)?.visitsAdults || 0,
          visitsMinors: (service as any)?.visitsMinors || 0,
          newAdults: (service as any)?.newAdults || 0,
          newMinors: (service as any)?.newMinors || 0
        });

        // Mostrar campos opcionales que tienen valores (y ocultar los que no)
        this.stateService.resetOptionalFieldsVisibility();
        this.stateService.showFieldsWithValues(this.stateService.getState());
      }),
      catchError(error => {
        console.error('Error loading divine service:', error);
        this.stateService.updateState({ isLoading: false });
        throw error;
      })
    );
  }

  /**
   * Guarda un servicio divino (crear o actualizar)
   */
  saveDivineService(formValue: any): Observable<DivineServiceDetailDto> {
    const state = this.stateService.getState();
    
    // Validaciones previas: verificar que organizationUnitId no esté vacío
    if (!formValue.organizationUnitId || formValue.organizationUnitId.trim() === '') {
      throw new Error('Invalid organization unit');
    }

    const dateAtMidnight = this.validationService.getDateAtMidnightUTC(formValue.date);
    this.stateService.updateState({ isSaving: true });

    if (state.isNewService) {
      return this.createDivineService(formValue, dateAtMidnight);
    } else {
      return this.updateDivineService(state.id!, formValue, dateAtMidnight);
    }
  }

  /**
   * Crea un nuevo servicio divino
   */
  private createDivineService(formValue: any, dateAtMidnight: string): Observable<DivineServiceDetailDto> {
    console.log('createDivineService - formValue:', formValue);
    
    const createDto: CreateDivineServiceDto = {
      startAt: dateAtMidnight,
      endAt: dateAtMidnight,
      divineServiceTypeId: formValue.type,
      bibleText: formValue.bibleText || '',
      memberInCharge: formValue.memberInCharge,
      location: formValue.location || '',
      organizationUnitId: formValue.organizationUnitId,
      totalMinors: formValue.totalMinors || 0,
      totalAdults: formValue.totalAdults || 0,
      notes: formValue.notes || '',
      offeringsARS: formValue.offeringsARS || 0,
      offeringsUSD: formValue.offeringsUSD || 0,
      visitsAdults: formValue.visitsAdults || 0,
      visitsMinors: formValue.visitsMinors || 0,
      newAdults: formValue.newAdults || 0,
      newMinors: formValue.newMinors || 0
    };

    console.log('createDivineService - createDto:', createDto);

    return this.apiService.createDivineService(createDto).pipe(
      tap(service => {
        this.stateService.updateState({
          id: service.id,
          isNewService: false,
          isSaving: false
        });
      }),
      catchError(error => {
        this.stateService.updateState({ isSaving: false });
        throw error;
      })
    );
  }

  /**
   * Actualiza un servicio divino existente
   */
  private updateDivineService(serviceId: string, formValue: any, dateAtMidnight: string): Observable<DivineServiceDetailDto> {
    const updateDto: UpdateDivineServiceDto = {
      startAt: dateAtMidnight,
      endAt: dateAtMidnight,
      divineServiceTypeId: formValue.type,
      bibleText: formValue.bibleText || '',
      memberInCharge: formValue.memberInCharge,
      location: formValue.location || '',
      organizationUnitId: formValue.organizationUnitId,
      totalMinors: formValue.totalMinors || 0,
      totalAdults: formValue.totalAdults || 0,
      notes: formValue.notes || '',
      offeringsARS: formValue.offeringsARS || 0,
      offeringsUSD: formValue.offeringsUSD || 0,
      visitsAdults: formValue.visitsAdults || 0,
      visitsMinors: formValue.visitsMinors || 0,
      newAdults: formValue.newAdults || 0,
      newMinors: formValue.newMinors || 0
    };

    return this.apiService.updateDivineService(serviceId, updateDto).pipe(
      tap(service => {
        this.stateService.updateState({ isSaving: false });
      }),
      catchError(error => {
        this.stateService.updateState({ isSaving: false });
        throw error;
      })
    );
  }

  /**
   * Actualiza el estado con cambios desde el formulario
   */
  updateState(updates: Partial<DivineServiceState>): void {
    this.stateService.updateState(updates);
  }

  /**
   * Actualiza la visibilidad de un campo opcional
   */
  updateFieldVisibility(field: keyof OptionalFieldsVisibility, visible: boolean): void {
    this.stateService.updateFieldVisibility(field, visible);
  }

  /**
   * Limpia el estado cuando se abandona el flujo
   */
  cleanup(): void {
    this.stateService.clearService();
    this.destroy$.next();
  }
}
